import React, { useState, useEffect } from 'react';
import { useBaseUrl } from '../../../utils/utils';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileContract, faSignature, faDownload, faEye, 
    faCheck, faTimes, faUser, faCalendarAlt, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

interface DigitalContract {
    contract_id: string;
    title: string;
    contract_type: string;
    status: string;
    contract_content: any;
    custom_terms: string;
    signatures_required: Array<{
        type: string;
        id: number;
        name: string;
    }>;
    signatures_received: Array<{
        signature_id: string;
        signer_type: string;
        signer_id: number;
        signer_name: string;
        signed_at: string;
    }>;
    created_date: string;
    effective_date: string;
    expiry_date: string;
    signed_date: string;
    version: string;
}

interface Props {
    contractId?: string;
    onClose?: () => void;
}

const DigitalContractViewer: React.FC<Props> = ({ contractId, onClose }) => {
    const [contract, setContract] = useState<DigitalContract | null>(null);
    const [loading, setLoading] = useState(false);
    const [signing, setSigning] = useState(false);
    const [signatureData, setSignatureData] = useState({
        signer_type: '',
        signer_id: '',
        signer_name: '',
        signer_email: '',
        signature_notes: ''
    });

    useEffect(() => {
        if (contractId) {
            fetchContract();
        }
    }, [contractId]);

    const fetchContract = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${useBaseUrl()}collaborations/digital-contracts/`, {
                params: {contract_id: contractId},
            });
            if (response.data.status === 'success') {
                setContract(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching contract:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch contract details'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSignContract = async () => {
        if (!contract || !signatureData.signer_type || !signatureData.signer_id || !signatureData.signer_name) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please fill in all required signature fields'
            });
            return;
        }

        setSigning(true);
        try {
            const response = await axios.post(`${useBaseUrl()}collaborations/digital-contracts/${contractId}/sign/`, {
                ...signatureData,
                signature_data: {
                    timestamp: new Date().toISOString(),
                    user_agent: navigator.userAgent,
                    notes: signatureData.signature_notes
                },
                signature_hash: `sig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Contract Signed!',
                    text: 'Your digital signature has been successfully applied to the contract'
                });
                fetchContract(); // Refresh contract data
            }
        } catch (error) {
            console.error('Error signing contract:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to sign contract. Please try again.'
            });
        } finally {
            setSigning(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const statusColors = {
            'draft': 'bg-secondary',
            'pending_signatures': 'bg-warning',
            'signed': 'bg-success',
            'active': 'bg-primary',
            'completed': 'bg-info',
            'terminated': 'bg-danger',
            'expired': 'bg-dark'
        };
        return statusColors[status.toLowerCase() as keyof typeof statusColors] || 'bg-secondary';
    };

    const getSignatureStatus = (signer: any) => {
        const signature = contract?.signatures_received.find(s => 
            s.signer_type === signer.type && s.signer_id === signer.id
        );
        return signature ? 'signed' : 'pending';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="text-center py-5">
                <FontAwesomeIcon icon={faFileContract} size="3x" className="text-muted mb-3" />
                <h5>No Contract Selected</h5>
                <p className="text-muted">Please select a contract to view</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">
                        <FontAwesomeIcon icon={faFileContract} className="me-3 text-primary" />
                        {contract.title}
                    </h3>
                    <div className="d-flex align-items-center gap-3">
                        <span className={`badge ${getStatusBadge(contract.status)} fs-6`}>
                            {contract.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-muted">Contract ID: {contract.contract_id}</span>
                        <span className="text-muted">Version: {contract.version}</span>
                    </div>
                </div>
                <div>
                    <button className="btn btn-outline-primary me-2">
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Download
                    </button>
                    {onClose && (
                        <button className="btn btn-outline-secondary" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                            Close
                        </button>
                    )}
                </div>
            </div>

            <div className="row">
                {/* Contract Content */}
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">Contract Details</h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Contract Type</label>
                                    <p>{contract.contract_type.replace('_', ' ')}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Created Date</label>
                                    <p>{new Date(contract.created_date).toLocaleDateString()}</p>
                                </div>
                            </div>

                            {contract.effective_date && (
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Effective Date</label>
                                        <p>{new Date(contract.effective_date).toLocaleDateString()}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Expiry Date</label>
                                        <p>{new Date(contract.expiry_date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label fw-bold">Contract Terms</label>
                                <div className="border rounded p-3 bg-light">
                                    <pre className="mb-0">{JSON.stringify(contract.contract_content, null, 2)}</pre>
                                </div>
                            </div>

                            {contract.custom_terms && (
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Custom Terms</label>
                                    <div className="border rounded p-3 bg-light">
                                        <p className="mb-0">{contract.custom_terms}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Signatures Panel */}
                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faSignature} className="me-2" />
                                Digital Signatures
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Required Signatures</label>
                                {contract.signatures_required.map((signer, index) => {
                                    const status = getSignatureStatus(signer);
                                    return (
                                        <div key={index} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
                                            <div>
                                                <div className="fw-bold">{signer.name}</div>
                                                <small className="text-muted">{signer.type}</small>
                                            </div>
                                            <span className={`badge ${status === 'signed' ? 'bg-success' : 'bg-warning'}`}>
                                                <FontAwesomeIcon icon={status === 'signed' ? faCheck : faTimes} className="me-1" />
                                                {status === 'signed' ? 'Signed' : 'Pending'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {contract.status === 'pending_signatures' && (
                                <div className="border-top pt-3">
                                    <h6>Sign This Contract</h6>
                                    <div className="mb-3">
                                        <label className="form-label">Signer Type</label>
                                        <select
                                            className="form-select"
                                            value={signatureData.signer_type}
                                            onChange={(e) => setSignatureData({...signatureData, signer_type: e.target.value})}
                                        >
                                            <option value="">Select Type</option>
                                            <option value="clinic">Clinic</option>
                                            <option value="patient">Patient</option>
                                            <option value="professional">Professional</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Signer ID</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={signatureData.signer_id}
                                            onChange={(e) => setSignatureData({...signatureData, signer_id: e.target.value})}
                                            placeholder="Enter your ID"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Signer Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={signatureData.signer_name}
                                            onChange={(e) => setSignatureData({...signatureData, signer_name: e.target.value})}
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={signatureData.signer_email}
                                            onChange={(e) => setSignatureData({...signatureData, signer_email: e.target.value})}
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Notes (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            value={signatureData.signature_notes}
                                            onChange={(e) => setSignatureData({...signatureData, signature_notes: e.target.value})}
                                            rows={3}
                                            placeholder="Any additional notes..."
                                        />
                                    </div>
                                    <button
                                        className="btn btn-success w-100"
                                        onClick={handleSignContract}
                                        disabled={signing}
                                    >
                                        {signing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Signing...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSignature} className="me-2" />
                                                Sign Contract
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {contract.status === 'signed' && (
                                <div className="text-center text-success">
                                    <FontAwesomeIcon icon={faCheck} size="2x" className="mb-2" />
                                    <h6>Contract Fully Signed</h6>
                                    <p className="text-muted">All parties have signed this contract</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contract Security Info */}
                    <div className="card mt-3">
                        <div className="card-header">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                Security Information
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                                <small className="text-muted">IP Address: {navigator.userAgent}</small>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-muted" />
                                <small className="text-muted">Last Updated: {new Date().toLocaleDateString()}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DigitalContractViewer;
